"use client";

import { useMemo } from "react";
import { SUPPORTED_EXCHANGES } from "@cede/types";
import { API_ENDPOINT, Rfc9457Error } from "@cede/utils";
import { WALLET_STATUS } from "@/constants/wallet";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useApi } from "@/hooks/core/useApi";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import { useHubUser } from "@/hooks/user/useHubUser";
import { buildExtendedStarkKeyTypedData, deriveExtendedKeys } from "@/utils/airdrop-farming/extended-account";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useAccount, useSignMessage } from "wagmi";

export type ExtendedSessionStatus =
  | "checking-wallet"
  | "needs-wallet"
  | "needs-approval"
  | "checking-activation"
  | "ready";

/**
 * Hook for managing Extended connection flow (two-phase, like GRVT).
 *
 * Flow:
 * 1. User signs EIP-712 "STARK Key" message → derives StarkNet private key
 * 2. Frontend calls prepare endpoint → gets an L1 auth message to sign
 * 3. User signs the message (personal_sign) with their EVM wallet
 * 4. Frontend calls authorize endpoint with the signature
 * 5. Backend creates API key on Extended, fetches account data using the key, stores credentials
 *
 * Security: The derived StarkNet key only authorises trading on Extended — it cannot
 * withdraw funds from the EVM wallet.
 */
export function useExtendedConnection() {
  const { auth, hubUser, walletStatus } = useHubUser();
  const apiHelper = useApi();
  const { address: connectedAddress, isConnected: isWalletConnected } = useAccount();
  const { authenticated } = useAuthenticationProvider();
  const queryClient = useQueryClient();
  const { signTypedDataAsync } = useSignTypedDataWithConnection();
  const { signMessageAsync } = useSignMessage();

  const extendedAccounts = useMemo(
    () => hubUser?.exchangeAccounts?.filter((a) => a.exchangeId === SUPPORTED_EXCHANGES.EXTENDED) ?? [],
    [hubUser?.exchangeAccounts],
  );

  const hasExtendedAccount = extendedAccounts.length > 0;

  const {
    mutateAsync: connectExtended,
    isPending: isConnecting,
    error: connectionError,
    reset: resetConnectionError,
  } = useMutation({
    mutationFn: async () => {
      if (!connectedAddress) throw new Error("Wallet not connected");

      // Step 1: Sign EIP-712 AccountCreation message to derive keys (matches Extended SDK)
      const typedData = buildExtendedStarkKeyTypedData(connectedAddress);
      const signature = await signTypedDataAsync(typedData);

      // Step 2: Derive StarkNet private key from signature
      const { privateKey } = deriveExtendedKeys(signature);

      // Step 3: Call prepare endpoint — backend stores data + returns message to sign
      const prepareResult = await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_EXTENDED_PREPARE, {
        bodyParams: {
          starknetPrivateKey: privateKey,
          walletAddress: connectedAddress,
        },
      });

      const { prepareId, messageToSign } = prepareResult as {
        prepareId: string;
        messageToSign: string;
      };

      // Step 4: Sign the L1 auth message with personal_sign
      const l1Signature = await signMessageAsync({ message: messageToSign });

      // Step 5: Call authorize endpoint — backend creates API key, fetches account data, stores credentials
      await apiHelper.postAuth(auth, API_ENDPOINT.EXCHANGE_ACCOUNT_EXTENDED_AUTHORIZE, {
        bodyParams: {
          prepareId,
          l1Signature,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      enqueueSnackbar("Extended account connected successfully", { variant: "success" });
    },
    onError: (error) => {
      if (error instanceof Rfc9457Error && error.details.type === "EXTENDED_ACCOUNT_NOT_FOUND") {
        enqueueSnackbar(
          "No Extended account found. Please create one at https://extended.exchange/ before connecting.",
          {
            variant: "warning",
          },
        );
        return;
      }
      const msg = error instanceof Error ? error.message : "Failed to connect Extended";
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const sessionStatus: ExtendedSessionStatus =
    walletStatus === WALLET_STATUS.INITIALIZING || walletStatus === WALLET_STATUS.CONNECTING
      ? "checking-wallet"
      : walletStatus === WALLET_STATUS.DISCONNECTED
        ? "needs-wallet"
        : isConnecting
          ? "checking-activation"
          : hasExtendedAccount
            ? "ready"
            : "needs-approval";

  return {
    // Wallet state
    connectedAddress,
    isWalletConnected,

    // Account state
    extendedAccounts,
    hasExtendedAccount,

    // Actions
    connectExtended,
    isConnecting,
    connectionError,
    resetConnectionError,

    // Session status
    sessionStatus,

    // ── Shape compatible with ExchangeConnection interface ──
    agentData: {
      accountId: extendedAccounts[0]?.id ?? undefined,
      agentAddress: extendedAccounts[0]?.publicApiKey ?? "",
      agentName: "",
    },
    hasAgentData: hasExtendedAccount,
    isLoadingAgent: false,
    isCheckingEligibility: false,
    isNotEligibleError: false,
    eligibilityData: { eligible: true } as const,
    isEligible: true,
    approveAgent: () => connectExtended(),
    isApproving: isConnecting,
    approvalError: connectionError,
    resetApprovalError: resetConnectionError,
    updateHyperliquidAgentStatus: async () => {},
    isUpdatingAgentStatus: false,
    isLoading: isConnecting,
    walletStatus,
    authenticated,
  };
}
